import { Fact, FactClassification, FactSchema } from 'services/fact/fact.model';
import { LegalRequest } from 'services/legal-request/legal-request.model';
import { Tag } from 'services/tag/tag.model';
import { Thesis, ThesisReference } from 'services/thesis/thesis.model';
import { Topic } from 'services/topic/topic.model';
import { AuditUser } from 'services/user/user.model';
import { MOCK_PEOPLE, MOCK_VERTICAL_INFO, productSetupIdOf } from '../shared/mock-context.const';
import {
	MOCK_FACT_ROSTER,
	MOCK_LEGAL_REQUEST_ROSTER,
	MOCK_TAG_ROSTER,
	MOCK_THESIS_ROSTER,
	MOCK_TOPIC_ROSTER,
} from '../shared/mock-knowledge-roster.const';

/**
 * Base de conhecimento da Contestação: entidades completas montadas a partir do roster
 * compartilhado (mesmos ids/nomes usados nas revisões e nos templates).
 *
 * Escopo: item **sem** `productSetupId` é global (cadastrado pelo admin); item com
 * `productSetupId` pertence ao produto do gestor (Contestação · Trabalhista).
 */

/** Produto do gestor dono dos itens não globais */
export const KNOWLEDGE_BASE_PRODUCT_SETUP_ID = productSetupIdOf('trabalhista', 'contestacao');

export const KNOWLEDGE_BASE_VERTICAL = {
	id: MOCK_VERTICAL_INFO['trabalhista'].id,
	name: MOCK_VERTICAL_INFO['trabalhista'].name,
};

type Scope = 'global' | 'manager';

interface AuditFields {
	createdAt: string;
	updatedAt: string;
	createdBy: AuditUser;
	updatedBy: AuditUser;
}

/** Auditoria da semente: o índice escalona as datas para a ordenação por criação fazer sentido */
function seedAudit(scope: Scope, index: number): AuditFields {
	const author = scope === 'global' ? MOCK_PEOPLE.admin : MOCK_PEOPLE.gerente;
	const created = new Date(Date.UTC(2026, 1, 2 + index * 3, 13, 15));
	const updated = new Date(Date.UTC(2026, 6, 1 + index * 2, 17, 40));
	return {
		createdAt: created.toISOString(),
		updatedAt: updated.toISOString(),
		createdBy: author,
		updatedBy: index % 3 === 0 ? MOCK_PEOPLE.juliana : author,
	};
}

function scopeFields(scope: Scope): { productSetupId?: string } {
	return scope === 'manager' ? { productSetupId: KNOWLEDGE_BASE_PRODUCT_SETUP_ID } : {};
}

/** Slug no formato do backend: minúsculas, sem acento, separado por hífen */
export function slugify(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

function pick<T extends { id: string }>(items: T[], ids: readonly string[]): T[] {
	return ids
		.map((id) => items.find((item) => item.id === id))
		.filter((item): item is T => item !== undefined);
}

// ─── Tags ────────────────────────────────────────────────────────────────────

const TAG_SCOPE: Record<string, Scope> = {
	'tag-001': 'global',
	'tag-002': 'global',
	'tag-003': 'global',
	'tag-004': 'global',
	'tag-005': 'manager',
	'tag-006': 'manager',
};

export const MOCK_TAGS: Tag[] = MOCK_TAG_ROSTER.map((tag, index) => ({
	id: tag.id,
	name: tag.name,
	slug: slugify(tag.name),
	...scopeFields(TAG_SCOPE[tag.id]),
	...seedAudit(TAG_SCOPE[tag.id], index),
}));

// ─── Fatos ───────────────────────────────────────────────────────────────────

interface FactSeed {
	id: string;
	slug: string;
	name: string;
	classification: FactClassification;
	scope: Scope;
	description: string;
	schema: FactSchema;
	examples: string[] | number[];
	tagIds: string[];
}

const MOTIVOS_RESCISAO = [
	'Dispensa sem justa causa',
	'Dispensa por justa causa',
	'Pedido de demissão',
	'Rescisão indireta',
	'Acordo entre as partes (art. 484-A da CLT)',
	'Término de contrato por prazo determinado',
];

const FACT_DETAILS: Record<
	(typeof MOCK_FACT_ROSTER)[number]['id'],
	Omit<FactSeed, 'id' | 'slug' | 'name' | 'classification'>
> = {
	'fact-001': {
		scope: 'global',
		description:
			'Data de início do contrato de trabalho informada pelo reclamante na petição inicial.',
		schema: { type: 'string', format: 'date', description: 'Data de admissão alegada' },
		examples: ['2018-03-12', '2021-07-01'],
		tagIds: ['tag-004', 'tag-006'],
	},
	'fact-002': {
		scope: 'global',
		description:
			'Data do término do contrato de trabalho (último dia trabalhado ou fim do aviso prévio) informada na inicial.',
		schema: { type: 'string', format: 'date', description: 'Data de demissão alegada' },
		examples: ['2025-11-28', '2026-02-15'],
		tagIds: ['tag-002', 'tag-004'],
	},
	'fact-003': {
		scope: 'global',
		description:
			'Jornada de trabalho descrita pelo reclamante: horários de entrada e saída, dias trabalhados por semana e intervalo intrajornada usufruído.',
		schema: {
			type: 'object',
			description: 'Jornada alegada na petição inicial',
			properties: {
				horarioEntrada: { type: 'string', format: 'time', description: 'Horário de entrada' },
				horarioSaida: { type: 'string', format: 'time', description: 'Horário de saída' },
				diasPorSemana: {
					type: 'integer',
					minimum: 1,
					maximum: 7,
					description: 'Dias trabalhados por semana',
				},
				intervaloMinutos: {
					type: 'integer',
					minimum: 0,
					maximum: 240,
					description: 'Intervalo intrajornada usufruído, em minutos',
				},
			},
			required: ['horarioEntrada', 'horarioSaida'],
		},
		examples: [
			'Segunda a sábado, das 7h às 19h, com 30 minutos de intervalo',
			'Segunda a sexta, das 14h às 23h30, sem intervalo',
		],
		tagIds: ['tag-001'],
	},
	'fact-004': {
		scope: 'manager',
		description:
			'Indica se a reclamada juntou aos autos os cartões de ponto de todo o período contratual.',
		schema: { type: 'boolean', description: 'Cartões de ponto disponíveis nos subsídios' },
		examples: [
			'Sim — espelhos de ponto de mar/2021 a nov/2025',
			'Não — controle manual extraviado',
		],
		tagIds: ['tag-001'],
	},
	'fact-005': {
		scope: 'global',
		description: 'Modalidade de extinção do contrato de trabalho alegada pelo reclamante.',
		schema: { type: 'string', enum: MOTIVOS_RESCISAO, description: 'Motivo da rescisão' },
		examples: ['Dispensa sem justa causa', 'Rescisão indireta'],
		tagIds: ['tag-002'],
	},
	'fact-006': {
		scope: 'manager',
		description:
			'Indica se o Termo de Rescisão do Contrato de Trabalho (TRCT) foi assinado pelo empregado e consta nos subsídios.',
		schema: { type: 'boolean', description: 'TRCT assinado pelo empregado' },
		examples: ['Sim — TRCT assinado em 05/12/2025', 'Não — TRCT sem assinatura'],
		tagIds: ['tag-002'],
	},
	'fact-007': {
		scope: 'manager',
		description:
			'Registro fotográfico do posto de trabalho enviado pelo cliente (relógio de ponto, EPIs e condições do ambiente).',
		schema: { type: 'boolean' },
		examples: [],
		tagIds: ['tag-005'],
	},
};

/** Fatos extras (fora do roster) para a base não ficar rala */
const EXTRA_FACTS: FactSeed[] = [
	{
		id: 'fact-008',
		slug: 'ultimo-salario',
		name: 'Último salário',
		classification: 'ALEGACAO',
		scope: 'global',
		description: 'Último salário-base mensal informado pelo reclamante, em reais.',
		schema: { type: 'number', minimum: 0, multipleOf: 0.01, description: 'Salário em R$' },
		examples: [2850.5, 4200],
		tagIds: ['tag-002', 'tag-003'],
	},
	{
		id: 'fact-009',
		slug: 'danos-morais-alegados',
		name: 'Fatos geradores do dano moral',
		classification: 'ALEGACAO',
		scope: 'global',
		description: 'Condutas que, segundo o reclamante, teriam causado o dano moral indenizável.',
		schema: {
			type: 'array',
			items: {
				type: 'string',
				enum: [
					'Assédio moral',
					'Atraso salarial',
					'Acidente de trabalho',
					'Revista íntima',
					'Cobrança abusiva de metas',
					'Dispensa discriminatória',
				],
			},
			minItems: 1,
			uniqueItems: true,
		},
		examples: ['Assédio moral', 'Cobrança abusiva de metas'],
		tagIds: ['tag-003'],
	},
	{
		id: 'fact-010',
		slug: 'data-pagamento-rescisao',
		name: 'Data de pagamento das verbas rescisórias',
		classification: 'SUBSIDIO',
		scope: 'manager',
		description:
			'Data do depósito das verbas rescisórias conforme comprovante bancário enviado pelo cliente.',
		schema: { type: 'string', format: 'date', description: 'Data do comprovante de pagamento' },
		examples: ['2025-12-05'],
		tagIds: ['tag-002'],
	},
	{
		id: 'fact-011',
		slug: 'acordo-compensacao',
		name: 'Acordo de compensação de jornada',
		classification: 'SUBSIDIO',
		scope: 'manager',
		description:
			'Indica se existe acordo individual ou norma coletiva autorizando o banco de horas.',
		schema: { type: 'boolean', description: 'Acordo de banco de horas vigente' },
		examples: ['Sim — ACT 2024/2025, cláusula 12ª', 'Não'],
		tagIds: ['tag-001'],
	},
];

const FACT_SEEDS: FactSeed[] = [
	...MOCK_FACT_ROSTER.map((fact) => ({
		id: fact.id,
		slug: fact.slug,
		name: fact.name,
		classification: fact.classification,
		...FACT_DETAILS[fact.id],
	})),
	...EXTRA_FACTS,
];

export const MOCK_FACTS: Fact[] = FACT_SEEDS.map(({ scope, tagIds, ...fact }, index) => ({
	...fact,
	...scopeFields(scope),
	...seedAudit(scope, index),
	tags: pick(MOCK_TAGS, tagIds),
	verticalId: KNOWLEDGE_BASE_VERTICAL.id,
	vertical: KNOWLEDGE_BASE_VERTICAL,
}));

// ─── Pedidos ─────────────────────────────────────────────────────────────────

interface LegalRequestSeed {
	id: string;
	name: string;
	scope: Scope;
	description: string;
	tagIds: string[];
}

const LEGAL_REQUEST_DETAILS: Record<
	(typeof MOCK_LEGAL_REQUEST_ROSTER)[number]['id'],
	Omit<LegalRequestSeed, 'id' | 'name'>
> = {
	'lr-001': {
		scope: 'global',
		description:
			'Pagamento das horas excedentes à 8ª diária e 44ª semanal com adicional de 50%, e reflexos em DSR, férias + 1/3, 13º salário, aviso prévio e FGTS + 40%.',
		tagIds: ['tag-001'],
	},
	'lr-002': {
		scope: 'global',
		description:
			'Adicional de 20% sobre as horas trabalhadas entre 22h e 5h, com observância da hora noturna reduzida (art. 73 da CLT) e reflexos.',
		tagIds: ['tag-001'],
	},
	'lr-003': {
		scope: 'global',
		description:
			'Multa equivalente a um salário do empregado pelo atraso no pagamento das verbas rescisórias além do prazo de 10 dias (art. 477, §§ 6º e 8º, da CLT).',
		tagIds: ['tag-002'],
	},
	'lr-004': {
		scope: 'global',
		description:
			'Indenização por danos extrapatrimoniais decorrentes de ofensa à honra, à imagem ou à integridade do trabalhador (arts. 223-A a 223-G da CLT).',
		tagIds: ['tag-003'],
	},
	'lr-005': {
		scope: 'global',
		description:
			'Honorários advocatícios de sucumbência entre 5% e 15% sobre o valor da liquidação (art. 791-A da CLT).',
		tagIds: [],
	},
};

const EXTRA_LEGAL_REQUESTS: LegalRequestSeed[] = [
	{
		id: 'lr-006',
		name: 'Adicional de insalubridade',
		scope: 'manager',
		description:
			'Adicional de insalubridade em grau médio ou máximo sobre o salário mínimo, por exposição a agentes nocivos sem EPI eficaz (art. 192 da CLT).',
		tagIds: ['tag-005'],
	},
	{
		id: 'lr-007',
		name: 'Reconhecimento de vínculo empregatício',
		scope: 'manager',
		description:
			'Declaração do vínculo de emprego com anotação da CTPS no período trabalhado como prestador de serviços (arts. 2º e 3º da CLT).',
		tagIds: ['tag-006'],
	},
];

const LEGAL_REQUEST_SEEDS: LegalRequestSeed[] = [
	...MOCK_LEGAL_REQUEST_ROSTER.map((legalRequest) => ({
		id: legalRequest.id,
		name: legalRequest.name,
		...LEGAL_REQUEST_DETAILS[legalRequest.id],
	})),
	...EXTRA_LEGAL_REQUESTS,
];

export const MOCK_LEGAL_REQUESTS: LegalRequest[] = LEGAL_REQUEST_SEEDS.map(
	({ scope, tagIds, ...legalRequest }, index) => ({
		...legalRequest,
		...scopeFields(scope),
		...seedAudit(scope, index),
		isGlobal: scope === 'global',
		verticalId: KNOWLEDGE_BASE_VERTICAL.id,
		tags: pick(MOCK_TAGS, tagIds),
	}),
);

// ─── Teses ───────────────────────────────────────────────────────────────────

interface ThesisDetails {
	scope: Scope;
	description: string;
	content: string;
	ruleIncludes: object;
	ruleExcludes?: object;
	/** Fatos das regras + os usados apenas como variáveis do conteúdo */
	factIds: string[];
}

const THESIS_DETAILS: Record<(typeof MOCK_THESIS_ROSTER)[number]['id'], ThesisDetails> = {
	'thesis-001': {
		scope: 'global',
		description:
			'Aplicável quando a inicial pede horas extras sem indicar a jornada efetivamente cumprida.',
		ruleIncludes: { and: [{ '!': [{ var: 'jornada-alegada' }] }] },
		factIds: ['fact-003'],
		content:
			'<p><strong>DA INÉPCIA DA PETIÇÃO INICIAL — PEDIDO GENÉRICO</strong></p>' +
			'<p>O reclamante postula o pagamento de horas extras sem, contudo, declinar a jornada que alega ter cumprido, os dias da semana em que teria havido labor extraordinário ou o período de apuração. Trata-se de pedido manifestamente genérico, que impede o exercício do contraditório e da ampla defesa pela reclamada.</p>' +
			'<p>Nos termos do art. 840, § 1º, da CLT, a reclamação escrita deve conter <em>breve exposição dos fatos de que resulte o dissídio</em> e pedido certo, determinado e com indicação de seu valor. Ausentes tais requisitos, impõe-se a extinção do feito sem resolução do mérito quanto ao pedido, na forma dos arts. 330, I e § 1º, II, e 485, I, do CPC, aplicados subsidiariamente (art. 769 da CLT).</p>',
	},
	'thesis-002': {
		scope: 'global',
		description:
			'Pronúncia da prescrição quinquenal das pretensões anteriores aos cinco anos do ajuizamento.',
		ruleIncludes: { and: [{ '<': [{ var: 'data-admissao' }, '2021-09-24'] }] },
		factIds: ['fact-001', 'fact-002'],
		content:
			'<p><strong>DA PRESCRIÇÃO QUINQUENAL</strong></p>' +
			'<p>O reclamante afirma ter sido admitido em {{data-admissao}} e dispensado em {{data-demissao}}. Considerando a data do ajuizamento da presente ação, encontram-se fulminadas pela prescrição quinquenal todas as pretensões relativas a parcelas exigíveis há mais de cinco anos.</p>' +
			'<p>Requer a reclamada, com fundamento no art. 7º, XXIX, da Constituição Federal, no art. 11 da CLT e na Súmula 308, I, do TST, a pronúncia da prescrição das pretensões anteriores ao quinquênio que antecede a propositura da ação, extinguindo-se o processo com resolução do mérito quanto a elas (art. 487, II, do CPC).</p>',
	},
	'thesis-003': {
		scope: 'manager',
		description:
			'Os cartões de ponto juntados registram a jornada real, com marcações variáveis e horas extras pagas.',
		ruleIncludes: {
			and: [
				{ '===': [{ var: 'possui-cartao-ponto' }, true] },
				{ '!!': [{ var: 'jornada-alegada' }] },
			],
		},
		factIds: ['fact-003', 'fact-004', 'fact-007'],
		content:
			'<p><strong>DA VALIDADE DOS CARTÕES DE PONTO</strong></p>' +
			'<p>Alega o reclamante que laborava das {{jornada-alegada.horarioEntrada}} às {{jornada-alegada.horarioSaida}}, sem a devida contraprestação das horas extras. Não lhe assiste razão.</p>' +
			'<p>A reclamada mantém controle eletrônico de jornada, nos moldes do art. 74, § 2º, da CLT, e junta aos autos a integralidade dos cartões de ponto do período contratual. Os registros apresentam <em>marcações variáveis</em> de entrada e saída, afastando a presunção de que trata a Súmula 338, III, do TST, e demonstram que eventuais horas extras foram corretamente quitadas nos recibos de pagamento.</p>' +
			'<p>O registro fotográfico do posto de trabalho evidencia a instalação do relógio de ponto em local de livre acesso aos empregados: {{foto-local-trabalho}}</p>' +
			'<p>Assim, cabia ao reclamante o ônus de desconstituir a prova documental (arts. 818, I, da CLT e 373, I, do CPC), do qual não se desincumbiu. Improcede o pedido.</p>',
	},
	'thesis-004': {
		scope: 'manager',
		description:
			'As horas excedentes foram compensadas por banco de horas previsto em norma coletiva.',
		ruleIncludes: {
			and: [
				{ '===': [{ var: 'acordo-compensacao' }, true] },
				{ '!!': [{ var: 'jornada-alegada' }] },
			],
		},
		ruleExcludes: { and: [{ '===': [{ var: 'possui-cartao-ponto' }, false] }] },
		factIds: ['fact-003', 'fact-004', 'fact-011'],
		content:
			'<p><strong>DA COMPENSAÇÃO DE JORNADA — BANCO DE HORAS</strong></p>' +
			'<p>Ainda que se admita a prestação de labor além da 8ª hora diária, as horas excedentes foram integralmente compensadas por meio de banco de horas instituído por acordo coletivo de trabalho, nos termos do art. 59, §§ 2º e 5º, da CLT.</p>' +
			'<p>Os extratos do banco de horas, juntados com a defesa, demonstram a apuração mensal dos créditos e débitos e a fruição de folgas compensatórias dentro do prazo legal. Não há, portanto, horas extras a serem pagas, sob pena de enriquecimento sem causa.</p>',
	},
	'thesis-005': {
		scope: 'global',
		description: 'A jornada cumprida não alcançava o período das 22h às 5h.',
		ruleIncludes: { and: [{ '!!': [{ var: 'jornada-alegada.horarioSaida' }] }] },
		factIds: ['fact-003'],
		content:
			'<p><strong>DA INEXISTÊNCIA DE LABOR NOTURNO</strong></p>' +
			'<p>O reclamante pleiteia adicional noturno afirmando encerrar a jornada às {{jornada-alegada.horarioSaida}}. Contudo, jamais laborou no período compreendido entre 22h de um dia e 5h do dia seguinte, conforme se extrai dos registros de jornada e da escala de trabalho do setor.</p>' +
			'<p>Não configurada a hipótese do art. 73, § 2º, da CLT, é indevido o adicional postulado, bem como os reflexos dele decorrentes.</p>',
	},
	'thesis-006': {
		scope: 'manager',
		description:
			'As verbas rescisórias foram pagas dentro do prazo de 10 dias do art. 477, § 6º, da CLT.',
		ruleIncludes: {
			and: [{ '!!': [{ var: 'data-pagamento-rescisao' }] }, { '!!': [{ var: 'data-demissao' }] }],
		},
		ruleExcludes: { and: [{ '===': [{ var: 'motivo-rescisao' }, 'Rescisão indireta'] }] },
		factIds: ['fact-002', 'fact-005', 'fact-010'],
		content:
			'<p><strong>DO PAGAMENTO TEMPESTIVO DAS VERBAS RESCISÓRIAS</strong></p>' +
			'<p>O contrato de trabalho foi extinto em {{data-demissao}}, na modalidade {{motivo-rescisao}}. As verbas rescisórias foram depositadas na conta do reclamante em {{data-pagamento-rescisao}}, conforme comprovante bancário anexo — portanto, dentro do prazo de 10 dias previsto no art. 477, § 6º, da CLT.</p>' +
			'<p>Inexistindo mora, é indevida a multa do art. 477, § 8º, da CLT.</p>',
	},
	'thesis-007': {
		scope: 'manager',
		description: 'O TRCT assinado dá quitação às parcelas nele discriminadas.',
		ruleIncludes: { and: [{ '===': [{ var: 'trct-assinado' }, true] }] },
		ruleExcludes: { and: [{ '===': [{ var: 'motivo-rescisao' }, 'Rescisão indireta'] }] },
		factIds: ['fact-005', 'fact-006'],
		content:
			'<p><strong>DA QUITAÇÃO PELO TRCT</strong></p>' +
			'<p>O reclamante firmou o Termo de Rescisão do Contrato de Trabalho, no qual constam, de forma discriminada, todas as parcelas rescisórias e os respectivos valores. A assinatura do termo, sem qualquer ressalva, confere eficácia liberatória às parcelas nele consignadas, nos termos do art. 477, § 2º, da CLT e da Súmula 330 do TST.</p>' +
			'<p>Requer-se, assim, o reconhecimento da quitação e, subsidiariamente, a dedução dos valores já pagos sob o mesmo título.</p>',
	},
	'thesis-008': {
		scope: 'global',
		description:
			'O reclamante não comprovou o ato ilícito, o dano e o nexo causal (arts. 818 da CLT e 373 do CPC).',
		ruleIncludes: { and: [{ '!!': [{ var: 'danos-morais-alegados' }] }] },
		factIds: ['fact-009'],
		content:
			'<p><strong>DA AUSÊNCIA DE PROVA DO DANO MORAL</strong></p>' +
			'<p>O reclamante atribui à reclamada condutas que teriam lhe causado abalo moral, sem apresentar, contudo, qualquer elemento de prova. A responsabilidade civil do empregador pressupõe a demonstração cumulativa do ato ilícito, do dano e do nexo de causalidade (arts. 186 e 927 do Código Civil).</p>' +
			'<p>Tratando-se de fato constitutivo do direito, o ônus da prova incumbe ao autor (art. 818, I, da CLT). Meros dissabores do cotidiano laboral não configuram dano extrapatrimonial indenizável. Improcede o pedido.</p>',
	},
	'thesis-009': {
		scope: 'manager',
		description:
			'Subsidiariamente, o valor pretendido excede os parâmetros do art. 223-G, § 1º, da CLT.',
		ruleIncludes: {
			and: [{ '!!': [{ var: 'danos-morais-alegados' }] }, { '>': [{ var: 'ultimo-salario' }, 0] }],
		},
		factIds: ['fact-008', 'fact-009'],
		content:
			'<p><strong>DO VALOR EXCESSIVO DA INDENIZAÇÃO</strong></p>' +
			'<p>Por cautela, caso se entenda devida alguma indenização, o valor pretendido revela-se desproporcional. O art. 223-G, § 1º, da CLT estabelece parâmetros objetivos para a fixação da reparação, tomando por base o último salário contratual do ofendido — no caso, R$ {{ultimo-salario}}.</p>' +
			'<p>Considerando a natureza da ofensa alegada, que, se existente, seria no máximo de natureza leve, a indenização não pode ultrapassar três vezes o último salário contratual (art. 223-G, § 1º, I, da CLT), observados os princípios da razoabilidade e da vedação ao enriquecimento sem causa.</p>',
	},
	'thesis-010': {
		scope: 'global',
		description:
			'Improcedentes os pedidos principais, não há sucumbência da reclamada; subsidiariamente, fixação no mínimo legal.',
		ruleIncludes: { and: [{ '!!': [{ var: 'data-demissao' }] }] },
		factIds: ['fact-002'],
		content:
			'<p><strong>DOS HONORÁRIOS ADVOCATÍCIOS</strong></p>' +
			'<p>Julgados improcedentes os pedidos formulados na inicial, não há falar em condenação da reclamada ao pagamento de honorários de sucumbência. Ao contrário, deve o reclamante ser condenado ao pagamento dos honorários do patrono da reclamada, nos termos do art. 791-A da CLT.</p>' +
			'<p>Subsidiariamente, em caso de procedência parcial, requer-se a fixação dos honorários no percentual mínimo de 5%, considerados o grau de zelo, a natureza e a importância da causa (art. 791-A, § 2º, da CLT), com a vedação de compensação entre os honorários (§ 3º).</p>',
	},
};

export const MOCK_THESES: Thesis[] = MOCK_THESIS_ROSTER.map((thesis, index) => {
	const { scope, factIds, ...details } = THESIS_DETAILS[thesis.id];
	return {
		id: thesis.id,
		name: thesis.name,
		slug: slugify(thesis.name),
		tipoTeseProcessual: thesis.tipo,
		...details,
		isGlobal: scope === 'global',
		...scopeFields(scope),
		...seedAudit(scope, index),
		verticalId: KNOWLEDGE_BASE_VERTICAL.id,
		tags: pick(MOCK_TAGS, thesis.tagIds),
		facts: pick(MOCK_FACTS, factIds),
		legalRequests: pick(MOCK_LEGAL_REQUESTS, thesis.legalRequestIds),
	};
});

/** Tópico de cada tese da semente (a tese não carrega o tópico no model do Hub) */
export const MOCK_THESIS_TOPIC_IDS: Record<string, string> = Object.fromEntries(
	MOCK_THESIS_ROSTER.map((thesis) => [thesis.id, thesis.topicId]),
);

export function toThesisReference(thesis: Thesis): ThesisReference {
	return {
		id: thesis.id,
		name: thesis.name,
		slug: thesis.slug,
		description: thesis.description,
		isGlobal: !thesis.productSetupId,
	};
}

// ─── Tópicos ─────────────────────────────────────────────────────────────────

const TOPIC_DETAILS: Record<
	(typeof MOCK_TOPIC_ROSTER)[number]['id'],
	{ scope: Scope; description: string }
> = {
	'topic-001': {
		scope: 'global',
		description:
			'Matérias processuais arguidas antes do mérito: inépcia, ilegitimidade, incompetência e litispendência.',
	},
	'topic-002': {
		scope: 'global',
		description:
			'Questões que antecedem a análise do mérito e podem extingui-lo, como prescrição e decadência.',
	},
	'topic-003': {
		scope: 'manager',
		description:
			'Horas extras, intervalo intrajornada, adicional noturno e compensação de jornada.',
	},
	'topic-004': {
		scope: 'manager',
		description: 'Aviso prévio, saldo de salário, férias, 13º, FGTS e multas dos arts. 467 e 477.',
	},
	'topic-005': {
		scope: 'manager',
		description: 'Indenizações por danos extrapatrimoniais e parâmetros do art. 223-G da CLT.',
	},
	'topic-006': {
		scope: 'global',
		description: 'Honorários de sucumbência, justiça gratuita e custas processuais.',
	},
};

export const MOCK_TOPICS: Topic[] = MOCK_TOPIC_ROSTER.map((topic, index) => {
	const { scope, description } = TOPIC_DETAILS[topic.id];
	return {
		id: topic.id,
		name: topic.name,
		description,
		enableBatchApproval: topic.enableBatchApproval,
		isGlobal: scope === 'global',
		...scopeFields(scope),
		...seedAudit(scope, index),
		theses: MOCK_THESES.filter((thesis) => MOCK_THESIS_TOPIC_IDS[thesis.id] === topic.id).map(
			toThesisReference,
		),
	};
});
